<template>
  <nav class="bg-white border-b border-gray-200 sticky top-0 z-[10000]">
    <div class="max-w-screen-3xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-22">
      <div class="flex items-center justify-between py-3">
        <!-- Leading content -->
        <div class="flex items-center gap-10">
          <NuxtLink
            to="/"
            class="flex-shrink-0"
            aria-label="Accueil Tayba Travel"
          >
            <TaybaLogo class="h-[50.5px] w-[119.03px]" />
          </NuxtLink>

          <!-- Desktop Navigation -->
          <div class="hidden lg:flex items-center gap-5">
            <!-- Accueil -->
            <NuxtLink
              to="/"
              class="font-['Outfit'] font-medium text-[16px] leading-[1.5] capitalize whitespace-nowrap transition-colors"
              :class="
                route.path === '/'
                  ? 'text-gray-400'
                  : 'text-gray-700 hover:text-gray-400'
              "
            >
              Accueil
            </NuxtLink>

            <!-- À propos -->
            <NuxtLink
              to="/a-propos"
              class="font-['Outfit'] font-medium text-[16px] leading-[1.5] whitespace-nowrap transition-colors"
              :class="
                route.path === '/a-propos'
                  ? 'text-gray-400'
                  : 'text-gray-700 hover:text-gray-400'
              "
            >
              À propos
            </NuxtLink>

            <!-- Omra Mega Menu -->
            <div
              class="relative"
              @mouseenter="openMegaMenu('omra')"
              @mouseleave="closeMegaMenu"
            >
              <NuxtLink
                to="/omra"
                class="bg-white flex items-center gap-1 rounded-md p-0 overflow-hidden group"
              >
                <span
                  class="font-['Outfit'] font-medium text-[16px] leading-[20px] capitalize whitespace-nowrap transition-colors"
                  :class="
                    route.path.startsWith('/omra') &&
                    !route.path.startsWith('/omra-ramadhan')
                      ? 'text-gray-400'
                      : 'text-gray-700 group-hover:text-gray-400'
                  "
                >
                  Omra
                </span>
                <IconChevronDown
                  class="w-2 h-2 text-gray-500 rotate-180 group-hover:rotate-0 group-hover:text-primary transition-all duration-200"
                  :class="{
                    'text-primary':
                      activeMegaMenu === 'omra' ||
                      (route.path.startsWith('/omra') &&
                        !route.path.startsWith('/omra-ramadhan')),
                  }"
                />
              </NuxtLink>
            </div>

            <!-- Omra Ramadhan Mega Menu -->
            <div
              class="relative"
              @mouseenter="openMegaMenu('omra-ramadhan')"
              @mouseleave="closeMegaMenu"
            >
              <NuxtLink
                to="/omra-ramadhan"
                class="bg-white flex items-center gap-1 rounded-md p-0 overflow-hidden group"
              >
                <span
                  class="font-['Outfit'] font-medium text-[16px] leading-[20px] capitalize whitespace-nowrap transition-colors"
                  :class="
                    route.path.startsWith('/omra-ramadhan')
                      ? 'text-gray-400'
                      : 'text-gray-700 group-hover:text-gray-400'
                  "
                >
                  Omra Ramadhan
                </span>
                <ChevronDownIcon
                  class="w-3.5 h-3.5 text-gray-500 group-hover:rotate-180 group-hover:text-primary transition-all duration-200"
                  :class="{
                    'rotate-180 text-primary':
                      activeMegaMenu === 'omra-ramadhan' ||
                      route.path.startsWith('/omra-ramadhan'),
                  }"
                />
              </NuxtLink>
            </div>

            <!-- Epargne with dropdown -->
            <div
              class="relative"
              @mouseenter="openMegaMenu('epargne')"
              @mouseleave="closeMegaMenu"
            >
              <NuxtLink
                to="/epargne"
                class="bg-white flex items-center gap-1 rounded-md p-0 overflow-hidden group"
              >
                <span
                  class="font-['Outfit'] font-medium text-[16px] leading-[20px] capitalize whitespace-nowrap transition-colors"
                  :class="
                    route.path.startsWith('/epargne')
                      ? 'text-gray-400'
                      : 'text-gray-700 group-hover:text-gray-400'
                  "
                >
                  Épargne
                </span>
                <ChevronDownIcon
                  class="w-3.5 h-3.5 text-gray-500 group-hover:text-primary transition-all duration-200"
                  :class="{ 'rotate-180': activeMegaMenu === 'epargne' }"
                />
              </NuxtLink>
            </div>

            <!-- Contact -->
            <NuxtLink
              to="/contact"
              class="font-['Outfit'] font-medium text-[16px] leading-[20px] capitalize whitespace-nowrap transition-colors"
              :class="
                route.path === '/contact'
                  ? 'text-gray-400'
                  : 'text-gray-700 hover:text-gray-400'
              "
            >
              Contact
            </NuxtLink>
          </div>
        </div>

        <!-- Trailing content - Desktop Actions -->
        <div class="hidden lg:flex items-center gap-2">
          <ClientOnly>
            <!-- Notification Center (All authenticated users) -->
            <NotificationCenter v-if="isAuthenticated" />

            <!-- If authenticated, show avatar menu -->
            <div v-if="isAuthenticated" class="relative">
              <UiDropdownMenu :items="userMenuItems" align="right">
                <template #button="{ open }">
                  <button
                    class="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20"
                    :class="
                      open
                        ? 'border border-[#d0a444]'
                        : 'border border-gray-300 hover:border-[#d0a444]'
                    "
                  >
                    <template v-if="auth.user?.image">
                      <img
                        :src="auth.user?.image as string"
                        alt=""
                        class="w-6 h-6 rounded-full object-cover"
                        referrerpolicy="no-referrer"
                      />
                    </template>
                    <template v-else>
                      <UserCircleIcon class="w-6 h-6 text-slate-900" />
                    </template>
                    <span
                      class="text-md font-['Outfit'] text-gray-700 max-w-[220px] truncate"
                    >
                      {{ auth.user?.name || auth.user?.email }}
                    </span>
                    <ChevronDownIcon
                      class="w-3 h-3 text-gray-500 transition-transform"
                      :class="{ 'rotate-180': open }"
                    />
                  </button>
                </template>
              </UiDropdownMenu>
            </div>
            <!-- Else show auth buttons -->
            <template v-else>
              <UiButton
                variant="outline"
                size="base"
                @click="openAuthWith('signin')"
              >
                Se connecter
              </UiButton>
              <UiButton
                variant="primary"
                size="base"
                @click="openAuthWith('signup')"
              >
                S'inscrire
              </UiButton>
            </template>
          </ClientOnly>
        </div>

        <!-- Mobile Menu Button & Actions -->
        <div class="lg:hidden flex items-center gap-0">
          <ClientOnly>
            <!-- Notification Bell for Mobile (Authenticated users) -->
            <NotificationCenter v-if="isAuthenticated" />

            <!-- Login Button for Mobile (Non-authenticated users) -->
            <button
              v-if="!isAuthenticated"
              @click="openAuthWith('signin')"
              class="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-300 hover:border-[#d0a444] transition-colors focus:outline-none focus:ring-2 focus:ring-[#d0a444]/30"
              aria-label="Se connecter"
            >
              <UserCircleIcon class="h-5 w-5 text-gray-700" />
              <span class="text-base font-medium font-['Outfit'] text-gray-700">
                Se connecter
              </span>
            </button>
          </ClientOnly>

          <button
            class="p-2 rounded-md text-gray-700 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
            @click="toggleMobileMenu"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4 7.5C4 6.67157 4.67157 6 5.5 6H26.5C27.3284 6 28 6.67157 28 7.5C28 8.32843 27.3284 9 26.5 9H5.5C4.67157 9 4 8.32843 4 7.5ZM4 16C4 15.1716 4.67157 14.5 5.5 14.5H26.5C27.3284 14.5 28 15.1716 28 16C28 16.8284 27.3284 17.5 26.5 17.5H5.5C4.67157 17.5 4 16.8284 4 16ZM4 24.5C4 23.6716 4.67157 23 5.5 23H26.5C27.3284 23 28 23.6716 28 24.5C28 25.3284 27.3284 26 26.5 26H5.5C4.67157 26 4 25.3284 4 24.5Z"
                fill="#111827"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mega Menu Dropdown Panel -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="activeMegaMenu"
          class="fixed left-0 right-0 top-[73px] z-50 hidden lg:block"
          @mouseenter="keepMegaMenuOpen"
          @mouseleave="closeMegaMenu"
        >
          <div class="bg-white border-b border-gray-200 shadow-lg">
            <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-4 py-6">
              <!-- Omra Mega Menu Content -->
              <div v-if="activeMegaMenu === 'omra'">
                <!-- Package Cards -->
                <div
                  class="grid gap-5"
                  :class="{
                    'grid-cols-1': omraPackages.length === 1,
                    'grid-cols-2': omraPackages.length === 2,
                    'grid-cols-3': omraPackages.length === 3,
                    'grid-cols-4': omraPackages.length === 4,
                    'grid-cols-5': omraPackages.length >= 5,
                  }"
                >
                  <NuxtLink
                    v-for="pack in omraPackages"
                    :key="pack.id"
                    :to="pack.href"
                    class="group"
                    @click="closeMegaMenuImmediate"
                  >
                    <div
                      class="relative aspect-[4/3] rounded-md overflow-hidden mb-2.5"
                    >
                      <img
                        :src="pack.image"
                        :alt="pack.fullTitle"
                        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div class="flex flex-wrap gap-1 mb-1.5">
                      <div
                        v-for="badge in pack.departures"
                        :key="badge"
                        class="inline-flex items-center gap-1 bg-[#FFF9EC] border border-[#D0A444] text-[#D0A444] px-2 py-1 rounded-[38px] text-xs font-medium font-outfit"
                      >
                        <UiIconsAirplaneIcon class="w-4 h-4" />
                        {{ badge }}
                      </div>
                    </div>
                    <h4
                      class="font-medium text-gray-900 group-hover:text-primary transition-colors"
                    >
                      {{ pack.fullTitle }}
                    </h4>
                    <span
                      class="text-sm text-primary font-medium flex items-center gap-1 mt-1 group-hover:gap-2 transition-all"
                    >
                      Plus de détails
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  </NuxtLink>
                </div>

                <!-- Bottom Links -->
                <div
                  class="flex items-center gap-6 mt-6 pt-5 border-t border-gray-100"
                >
                  <button
                    @click="openBrochure('omra')"
                    class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="underline">Consulter notre brochure</span>
                  </button>
                  <NuxtLink
                    to="/contact"
                    class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    @click="closeMegaMenuImmediate"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span class="underline"
                      >Contactez-nous pour plus d'informations</span
                    >
                  </NuxtLink>
                </div>
              </div>

              <!-- Omra Ramadhan Mega Menu Content -->
              <div
                v-if="activeMegaMenu === 'omra-ramadhan'"
                class="grid grid-cols-12 gap-6"
              >
                <!-- Package Cards -->
                <div class="col-span-9">
                  <div class="grid grid-cols-3 gap-6">
                    <NuxtLink
                      v-for="pack in ramadanPackages"
                      :key="pack.id"
                      :to="pack.href"
                      class="group"
                      @click="closeMegaMenuImmediate"
                    >
                      <div
                        class="relative aspect-[4/3] rounded-md overflow-hidden mb-2.5"
                      >
                        <img
                          :src="pack.image"
                          :alt="pack.fullTitle"
                          class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div class="flex flex-wrap gap-1 mb-1.5">
                        <div
                          v-for="period in pack.periods"
                          :key="period"
                          class="inline-flex items-center gap-1 bg-[#FFF9EC] border border-[#D0A444] text-[#D0A444] px-2 py-1 rounded-[38px] text-xs font-medium font-outfit"
                        >
                          <svg
                            class="w-3.5 h-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          {{ period }}
                        </div>
                      </div>
                      <h4
                        class="font-medium text-gray-900 group-hover:text-primary transition-colors"
                      >
                        {{ pack.fullTitle }}
                      </h4>
                      <span
                        class="text-sm text-primary font-medium flex items-center gap-1 mt-1 group-hover:gap-2 transition-all"
                      >
                        Plus de détails
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span>
                    </NuxtLink>
                  </div>

                  <!-- Bottom Links -->
                  <div
                    class="flex items-center gap-6 mt-6 pt-5 border-t border-gray-100"
                  >
                    <button
                      @click="openBrochure('ramadhan')"
                      class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span class="underline">Consulter notre brochure</span>
                    </button>
                    <NuxtLink
                      to="/contact"
                      class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                      @click="closeMegaMenuImmediate"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span class="underline"
                        >Contactez-nous pour plus d'informations</span
                      >
                    </NuxtLink>
                  </div>
                </div>

                <!-- Ramadhan Dates Sidebar -->
                <div class="col-span-3 border-l border-gray-100 pl-6">
                  <h4
                    class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3"
                  >
                    Dates du Ramadhan
                  </h4>
                  <div
                    class="bg-gradient-to-br from-primary-light to-surface-light rounded-lg p-4"
                  >
                    <div class="flex items-center gap-2 text-primary mb-2">
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                        />
                      </svg>
                      <span class="font-semibold">{{
                        ramadhanInfo.title
                      }}</span>
                    </div>
                    <p class="text-sm text-gray-700 font-medium">
                      {{ ramadhanInfo.dateRange }}
                    </p>
                    <p class="text-xs text-gray-500 mt-2">
                      Places limitées, réservez dès maintenant !
                    </p>
                  </div>
                </div>
              </div>

              <!-- Épargne Mega Menu Content -->
              <div v-if="activeMegaMenu === 'epargne'">
                <div class="grid grid-cols-3 gap-6">
                  <!-- Épargne Omra Card -->
                  <NuxtLink
                    to="/epargne/epargne-omra"
                    class="group relative bg-gradient-to-br from-[#FFF9EC] to-white border border-[#D0A444]/20 rounded-xl p-6 hover:shadow-lg hover:border-[#D0A444]/40 transition-all duration-300"
                    @click="closeMegaMenuImmediate"
                  >
                    <div class="flex items-start gap-4">
                      <div
                        class="flex-shrink-0 w-12 h-12 bg-[#D0A444]/10 rounded-lg flex items-center justify-center group-hover:bg-[#D0A444]/20 transition-colors"
                      >
                        <svg
                          width="17"
                          height="19"
                          viewBox="0 0 17 19"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5.96788 0.53021C7.79639 -0.14959 9.34424 -0.179979 11.0685 0.463804C11.9606 0.796925 12.6445 1.02716 13.0411 1.74896C13.1239 1.8997 13.2055 2.1091 13.2462 2.2763C13.3471 2.69128 13.2914 3.07744 13.1544 3.48236C13.0232 3.87011 12.8014 4.3184 12.5353 4.85834C12.5292 4.87066 12.5218 4.88269 12.5148 4.89447C12.2933 5.2635 12.1059 5.58822 11.8566 5.84173C12.2136 6.03518 12.4634 6.26636 12.8087 6.58294C14.448 8.08587 16.3347 10.643 16.9415 14.0849C17.1939 15.5168 16.6101 16.7667 15.6173 17.6357C14.6329 18.4972 13.2366 18.9999 11.7814 18.9999L5.21886 18.9999C3.76359 18.9999 2.36732 18.4973 1.38292 17.6357C0.390117 16.7667 -0.193705 15.5167 0.0586995 14.0849C0.665532 10.6431 2.55218 8.08588 4.19151 6.58294C4.53659 6.2666 4.78605 6.03509 5.14268 5.84173C4.89358 5.58827 4.70679 5.26328 4.48546 4.89447C4.47837 4.88266 4.47103 4.87068 4.46495 4.85834C4.21841 4.35807 4.01428 3.94456 3.8878 3.5888C3.75667 3.21995 3.69182 2.86811 3.75694 2.48822C3.80247 2.22289 3.92371 1.91144 4.06944 1.68548C4.46971 1.06511 5.11522 0.84725 5.96788 0.53021ZM7.5421 6.42962C6.64834 6.42962 6.31026 6.43529 5.90538 6.59076C5.49449 6.74859 5.32482 6.89985 4.86729 7.31927C3.34902 8.71114 1.60304 11.0825 1.04307 14.2587C0.86152 15.2889 1.26582 16.2033 2.0421 16.8827C2.82684 17.5695 3.98181 17.9999 5.21886 17.9999L11.7814 17.9999C13.0184 17.9999 14.1734 17.5695 14.9581 16.8827C15.7344 16.2033 16.1387 15.289 15.9571 14.2587C15.3972 11.0824 13.6512 8.71113 12.1329 7.31927C11.6754 6.89984 11.5058 6.7486 11.0948 6.59076C10.69 6.43531 10.3519 6.42962 9.45811 6.42962L7.5421 6.42962ZM10.7179 1.40033C9.23881 0.848178 7.93964 0.863822 6.31554 1.46771C5.35374 1.82534 5.09124 1.9454 4.90929 2.22748C4.837 2.33957 4.76516 2.52392 4.74229 2.65716C4.71621 2.80933 4.73248 2.97902 4.83018 3.25384C4.93119 3.53792 5.10065 3.88506 5.35167 4.39447C5.74427 5.04814 5.85529 5.19782 6.0753 5.29681C6.19586 5.35102 6.35531 5.389 6.63097 5.40912C6.91153 5.42957 7.27127 5.42962 7.78624 5.42962L9.21397 5.42962C9.72893 5.42962 10.0887 5.42957 10.3692 5.40912C10.6449 5.38901 10.8043 5.351 10.9249 5.29681C11.1449 5.19785 11.2552 5.04778 11.6476 4.39447C11.9196 3.84249 12.1033 3.46885 12.2071 3.16205C12.3065 2.86836 12.3162 2.68413 12.2745 2.51263C12.2546 2.4306 12.2044 2.30372 12.1642 2.23041C11.9816 1.8982 11.7187 1.77403 10.7179 1.40033Z"
                            fill="#D0A444"
                          />
                        </svg>
                      </div>
                      <div class="flex-1">
                        <h4
                          class="font-semibold text-gray-900 group-hover:text-[#D0A444] transition-colors text-lg mb-1"
                        >
                          Épargne Omra
                        </h4>
                        <p class="text-sm text-gray-600 mb-3">
                          Économisez chaque mois pour réaliser votre Omra. Plans
                          flexibles à partir de 50€/mois.
                        </p>
                        <span
                          class="text-sm text-[#D0A444] font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
                        >
                          Découvrir
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </NuxtLink>

                  <!-- Épargne Hajj Card -->
                  <NuxtLink
                    to="/epargne/epargne-hajj"
                    class="group relative bg-gradient-to-br from-[#FFF9EC] to-white border border-[#D0A444]/20 rounded-xl p-6 hover:shadow-lg hover:border-[#D0A444]/40 transition-all duration-300"
                    @click="closeMegaMenuImmediate"
                  >
                    <div class="flex items-start gap-4">
                      <div
                        class="flex-shrink-0 w-12 h-12 bg-[#D0A444]/10 rounded-lg flex items-center justify-center group-hover:bg-[#D0A444]/20 transition-colors"
                      >
                        <svg
                          width="17"
                          height="19"
                          viewBox="0 0 17 19"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5.96788 0.53021C7.79639 -0.14959 9.34424 -0.179979 11.0685 0.463804C11.9606 0.796925 12.6445 1.02716 13.0411 1.74896C13.1239 1.8997 13.2055 2.1091 13.2462 2.2763C13.3471 2.69128 13.2914 3.07744 13.1544 3.48236C13.0232 3.87011 12.8014 4.3184 12.5353 4.85834C12.5292 4.87066 12.5218 4.88269 12.5148 4.89447C12.2933 5.2635 12.1059 5.58822 11.8566 5.84173C12.2136 6.03518 12.4634 6.26636 12.8087 6.58294C14.448 8.08587 16.3347 10.643 16.9415 14.0849C17.1939 15.5168 16.6101 16.7667 15.6173 17.6357C14.6329 18.4972 13.2366 18.9999 11.7814 18.9999L5.21886 18.9999C3.76359 18.9999 2.36732 18.4973 1.38292 17.6357C0.390117 16.7667 -0.193705 15.5167 0.0586995 14.0849C0.665532 10.6431 2.55218 8.08588 4.19151 6.58294C4.53659 6.2666 4.78605 6.03509 5.14268 5.84173C4.89358 5.58827 4.70679 5.26328 4.48546 4.89447C4.47837 4.88266 4.47103 4.87068 4.46495 4.85834C4.21841 4.35807 4.01428 3.94456 3.8878 3.5888C3.75667 3.21995 3.69182 2.86811 3.75694 2.48822C3.80247 2.22289 3.92371 1.91144 4.06944 1.68548C4.46971 1.06511 5.11522 0.84725 5.96788 0.53021ZM7.5421 6.42962C6.64834 6.42962 6.31026 6.43529 5.90538 6.59076C5.49449 6.74859 5.32482 6.89985 4.86729 7.31927C3.34902 8.71114 1.60304 11.0825 1.04307 14.2587C0.86152 15.2889 1.26582 16.2033 2.0421 16.8827C2.82684 17.5695 3.98181 17.9999 5.21886 17.9999L11.7814 17.9999C13.0184 17.9999 14.1734 17.5695 14.9581 16.8827C15.7344 16.2033 16.1387 15.289 15.9571 14.2587C15.3972 11.0824 13.6512 8.71113 12.1329 7.31927C11.6754 6.89984 11.5058 6.7486 11.0948 6.59076C10.69 6.43531 10.3519 6.42962 9.45811 6.42962L7.5421 6.42962ZM10.7179 1.40033C9.23881 0.848178 7.93964 0.863822 6.31554 1.46771C5.35374 1.82534 5.09124 1.9454 4.90929 2.22748C4.837 2.33957 4.76516 2.52392 4.74229 2.65716C4.71621 2.80933 4.73248 2.97902 4.83018 3.25384C4.93119 3.53792 5.10065 3.88506 5.35167 4.39447C5.74427 5.04814 5.85529 5.19782 6.0753 5.29681C6.19586 5.35102 6.35531 5.389 6.63097 5.40912C6.91153 5.42957 7.27127 5.42962 7.78624 5.42962L9.21397 5.42962C9.72893 5.42962 10.0887 5.42957 10.3692 5.40912C10.6449 5.38901 10.8043 5.351 10.9249 5.29681C11.1449 5.19785 11.2552 5.04778 11.6476 4.39447C11.9196 3.84249 12.1033 3.46885 12.2071 3.16205C12.3065 2.86836 12.3162 2.68413 12.2745 2.51263C12.2546 2.4306 12.2044 2.30372 12.1642 2.23041C11.9816 1.8982 11.7187 1.77403 10.7179 1.40033Z"
                            fill="#D0A444"
                          />
                        </svg>
                      </div>
                      <div class="flex-1">
                        <h4
                          class="font-semibold text-gray-900 group-hover:text-[#D0A444] transition-colors text-lg mb-1"
                        >
                          Épargne Hajj
                        </h4>
                        <p class="text-sm text-gray-600 mb-3">
                          Préparez votre pèlerinage avec sérénité. Plans
                          personnalisés à partir de 60€/mois.
                        </p>
                        <span
                          class="text-sm text-[#D0A444] font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
                        >
                          Découvrir
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </NuxtLink>

                  <!-- Info Card -->
                  <div
                    class="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6"
                  >
                    <div class="flex items-start gap-4">
                      <div
                        class="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center"
                      >
                        <Icon
                          name="heroicons:question-mark"
                          class="w-6 h-6 text-gray-400"
                        />
                      </div>
                      <div class="flex-1">
                        <h4 class="font-semibold text-gray-900 text-lg mb-1">
                          Comment ça marche ?
                        </h4>
                        <ul class="text-sm text-gray-600 space-y-1.5">
                          <li class="flex items-center gap-2">
                            <span
                              class="w-1.5 h-1.5 bg-[#D0A444] rounded-full"
                            ></span>
                            Choisissez votre plan d'épargne
                          </li>
                          <li class="flex items-center gap-2">
                            <span
                              class="w-1.5 h-1.5 bg-[#D0A444] rounded-full"
                            ></span>
                            Versements mensuels automatiques
                          </li>
                          <li class="flex items-center gap-2">
                            <span
                              class="w-1.5 h-1.5 bg-[#D0A444] rounded-full"
                            ></span>
                            Utilisez votre épargne pour votre voyage
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Bottom Link -->
                <div
                  class="flex items-center gap-6 mt-6 pt-5 border-t border-gray-100"
                >
                  <NuxtLink
                    to="/epargne"
                    class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    @click="closeMegaMenuImmediate"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                    <span class="underline">Voir tous les plans d'épargne</span>
                  </NuxtLink>
                  <NuxtLink
                    to="/contact"
                    class="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    @click="closeMegaMenuImmediate"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span class="underline"
                      >Des questions ? Contactez-nous</span
                    >
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Mobile Menu - Full Screen Slide Panel -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mobileMenuOpen"
          class="fixed inset-0 bg-black/40 z-[60] lg:hidden"
          @click="closeMobileMenu"
        />
      </Transition>

      <Transition
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="mobileMenuOpen"
          class="fixed top-0 right-0 bottom-0 w-[85%] max-w-[320px] bg-white z-[70] lg:hidden flex flex-col shadow-xl"
        >
          <!-- Mobile Menu Header -->
          <div
            class="flex items-center justify-between p-4 border-b border-gray-200"
          >
            <NuxtLink to="/" @click="closeMobileMenu" class="flex-shrink-0">
              <TaybaLogo class="h-10 w-auto" />
            </NuxtLink>
            <button
              class="p-2 -mr-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              @click="closeMobileMenu"
            >
              <svg
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Mobile Menu Content -->
          <div class="flex-1 overflow-y-auto py-4">
            <div class="flex flex-col">
              <!-- Accueil Link -->
              <NuxtLink
                to="/"
                class="flex items-center px-5 py-4 font-['Outfit'] font-medium text-base text-gray-800 hover:text-[#d0a444] hover:bg-gray-50 transition-colors border-b border-gray-100"
                @click="closeMobileMenu"
              >
                Accueil
              </NuxtLink>

              <!-- À propos Link -->
              <NuxtLink
                to="/a-propos"
                class="flex items-center px-5 py-4 font-['Outfit'] font-medium text-base text-gray-800 hover:text-[#d0a444] hover:bg-gray-50 transition-colors border-b border-gray-100"
                @click="closeMobileMenu"
              >
                À propos
              </NuxtLink>

              <!-- Omra Accordion -->
              <div class="border-b border-gray-100">
                <button
                  class="flex items-center justify-between w-full px-5 py-4 text-left"
                  @click="toggleAccordion('omra')"
                >
                  <span
                    class="font-['Outfit'] font-medium text-base text-gray-800"
                    >Omra</span
                  >
                  <svg
                    class="w-5 h-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': openAccordion === 'omra' }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <Transition
                  enter-active-class="transition-all duration-200 ease-out"
                  enter-from-class="opacity-0 max-h-0"
                  enter-to-class="opacity-100 max-h-96"
                  leave-active-class="transition-all duration-150 ease-in"
                  leave-from-class="opacity-100 max-h-96"
                  leave-to-class="opacity-0 max-h-0"
                >
                  <div
                    v-if="openAccordion === 'omra'"
                    class="overflow-hidden bg-gray-50"
                  >
                    <!-- Link to all Omra packages -->
                    <NuxtLink
                      to="/omra"
                      class="block px-8 py-3 text-sm font-semibold text-[#d0a444] hover:bg-gray-100 transition-colors"
                      @click="closeMobileMenu"
                    >
                      Toutes les formules Omra
                    </NuxtLink>
                    <!-- Dynamic Omra packages -->
                    <NuxtLink
                      v-for="pack in omraPackages"
                      :key="pack.id"
                      :to="pack.href"
                      class="block px-8 py-3 text-sm text-gray-600 hover:text-[#d0a444] hover:bg-gray-100 transition-colors"
                      @click="closeMobileMenu"
                    >
                      {{ pack.fullTitle }}
                    </NuxtLink>
                    <!-- Brochure Link -->
                    <button
                      @click="openBrochureMobile('omra')"
                      class="w-full text-left px-8 py-3 text-sm text-[#d0a444] font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Consulter la brochure Omra
                    </button>
                  </div>
                </Transition>
              </div>

              <!-- Omra Ramadhan Accordion -->
              <div class="border-b border-gray-100">
                <button
                  class="flex items-center justify-between w-full px-5 py-4 text-left"
                  @click="toggleAccordion('omra-ramadhan')"
                >
                  <span
                    class="font-['Outfit'] font-medium text-base text-gray-800"
                    >Omra Ramadhan</span
                  >
                  <svg
                    class="w-5 h-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': openAccordion === 'omra-ramadhan' }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <Transition
                  enter-active-class="transition-all duration-200 ease-out"
                  enter-from-class="opacity-0 max-h-0"
                  enter-to-class="opacity-100 max-h-96"
                  leave-active-class="transition-all duration-150 ease-in"
                  leave-from-class="opacity-100 max-h-96"
                  leave-to-class="opacity-0 max-h-0"
                >
                  <div
                    v-if="openAccordion === 'omra-ramadhan'"
                    class="overflow-hidden bg-gray-50"
                  >
                    <!-- Link to all Omra Ramadhan packages -->
                    <NuxtLink
                      to="/omra-ramadhan"
                      class="block px-8 py-3 text-sm font-semibold text-[#d0a444] hover:bg-gray-100 transition-colors"
                      @click="closeMobileMenu"
                    >
                      Toutes les formules Ramadhan
                    </NuxtLink>
                    <!-- Dynamic Ramadhan packages -->
                    <NuxtLink
                      v-for="pack in ramadanPackages"
                      :key="pack.id"
                      :to="pack.href"
                      class="block px-8 py-3 text-sm text-gray-600 hover:text-[#d0a444] hover:bg-gray-100 transition-colors"
                      @click="closeMobileMenu"
                    >
                      {{ pack.fullTitle }}
                    </NuxtLink>
                    <!-- Show message if no Ramadhan packages -->
                    <div
                      v-if="ramadanPackages.length === 0"
                      class="px-8 py-3 text-sm text-gray-400 italic"
                    >
                      Formules bientôt disponibles
                    </div>
                    <!-- Brochure Link (always show if there are packages) -->
                    <button
                      v-if="ramadanPackages.length > 0"
                      @click="openBrochureMobile('ramadhan')"
                      class="w-full text-left px-8 py-3 text-sm text-[#d0a444] font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Consulter la brochure Ramadhan
                    </button>
                  </div>
                </Transition>
              </div>

              <!-- Epargne Accordion -->
              <div class="border-b border-gray-100">
                <button
                  class="flex items-center justify-between w-full px-5 py-4 text-left"
                  @click="toggleAccordion('epargne')"
                >
                  <span
                    class="font-['Outfit'] font-medium text-base text-gray-800"
                    >Épargne</span
                  >
                  <svg
                    class="w-5 h-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': openAccordion === 'epargne' }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <Transition
                  enter-active-class="transition-all duration-200 ease-out"
                  enter-from-class="opacity-0 max-h-0"
                  enter-to-class="opacity-100 max-h-96"
                  leave-active-class="transition-all duration-150 ease-in"
                  leave-from-class="opacity-100 max-h-96"
                  leave-to-class="opacity-0 max-h-0"
                >
                  <div
                    v-if="openAccordion === 'epargne'"
                    class="overflow-hidden bg-gray-50"
                  >
                    <NuxtLink
                      v-for="(item, index) in navigationItems[2]!.children"
                      :key="item.href"
                      :to="item.href"
                      :class="[
                        'block px-8 py-3 text-sm hover:bg-gray-100 transition-colors',
                        index === 0
                          ? 'font-semibold text-[#d0a444]'
                          : 'text-gray-600 hover:text-[#d0a444]',
                      ]"
                      @click="closeMobileMenu"
                    >
                      {{ item.label }}
                    </NuxtLink>
                  </div>
                </Transition>
              </div>

              <!-- Contact Link -->
              <NuxtLink
                to="/contact"
                class="flex items-center px-5 py-4 font-['Outfit'] font-medium text-base text-gray-800 hover:text-[#d0a444] hover:bg-gray-50 transition-colors border-b border-gray-100"
                @click="closeMobileMenu"
              >
                Contact
              </NuxtLink>
            </div>
          </div>

          <!-- Mobile Menu Footer - Auth Buttons -->
          <div class="p-4 border-t border-gray-200 bg-gray-50">
            <template v-if="isAuthenticated">
              <NuxtLink
                :to="mobileProfilePath"
                class="flex items-center gap-3 mb-4 px-1 hover:bg-gray-100 -mx-1 py-2 rounded-lg transition-colors"
                @click="mobileMenuOpen = false"
              >
                <template v-if="auth.user?.image">
                  <img
                    :src="auth.user?.image as string"
                    alt=""
                    class="w-10 h-10 rounded-full object-cover"
                    referrerpolicy="no-referrer"
                  />
                </template>
                <template v-else>
                  <div
                    class="w-10 h-10 rounded-full bg-[#d0a444]/10 flex items-center justify-center"
                  >
                    <UserCircleIcon class="w-6 h-6 text-[#d0a444]" />
                  </div>
                </template>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ auth.user?.name || "Utilisateur" }}
                  </p>
                  <p class="text-xs text-gray-500 truncate">
                    {{ auth.user?.email }}
                  </p>
                </div>
              </NuxtLink>
              <div class="flex flex-col gap-2">
                <UiButton
                  variant="outline"
                  size="base"
                  full-width
                  @click="goAccountMobile"
                >
                  Tableau de bord
                </UiButton>
                <UiButton
                  variant="ghost"
                  size="base"
                  full-width
                  class="text-red-600 hover:bg-red-50"
                  @click="onLogoutMobile"
                >
                  Se déconnecter
                </UiButton>
              </div>
            </template>
            <template v-else>
              <div class="flex flex-col gap-2">
                <UiButton
                  variant="outline"
                  size="base"
                  full-width
                  @click="openAuthMobile('signin')"
                >
                  Se connecter
                </UiButton>
                <UiButton
                  variant="primary"
                  size="base"
                  full-width
                  @click="openAuthMobile('signup')"
                >
                  S'inscrire
                </UiButton>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Auth Modal -->
    <AuthModal
      :open="openAuth"
      :defaultTab="authTab"
      @close="openAuth = false"
    />

    <!-- Brochure Modal -->
    <BrochureModal
      :isOpen="showBrochureModal"
      :type="brochureType"
      @close="closeBrochureModal"
    />
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { NavigationItem } from "~/types";
import TaybaLogo from "../ui/media/TaybaLogo.vue";
import UiButton from "../ui/base/Button.vue";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import AuthModal from "~/components/auth/AuthModal.vue";
import BrochureModal from "~/components/ui/BrochureModal.vue";
import {
  UserCircleIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
} from "@heroicons/vue/24/outline";
import UiDropdownMenu from "../ui/navigation/DropdownMenu.vue";
import { useAuthStore } from "~/stores/auth";
import { useBookingStore } from "~/stores/booking";
import { useNotifications } from "~/composables/notifications/useNotifications";
import type { Package } from "~/types/package";
import IconChevronDown from "../icons/IconChevronDown.vue";
import {
  getMinCombinationPrice,
  getRamadanDepartureDates,
} from "~/utils/hotel-combination-pricing";

// Route for active state
const route = useRoute();

// Lazy load NotificationCenter only when needed
const NotificationCenter = defineAsyncComponent(
  () => import("~/components/notifications/NotificationCenter.vue"),
);

// Fetch packages for mega menu
const { data: packagesData } = await useFetch<{ packages: Package[] }>(
  "/api/packages",
  {
    key: "nav-packages",
    lazy: true,
  },
);

// Computed mega menu data from API
const omraPackages = computed(() => {
  const packages = packagesData.value?.packages || [];
  return packages
    .filter(
      (pkg) => !pkg.isRamadan && (pkg.category === "omra" || !pkg.category),
    )
    .sort((a, b) => getPackageMinPrice(a) - getPackageMinPrice(b))
    .slice(0, 5) // Limit to 5 packages
    .map((pkg) => {
      const title = (pkg.title || pkg.name || "").toUpperCase();
      return {
        id: pkg.id,
        fullTitle: title,
        href: `/omra/${pkg.slug}`,
        image: pkg.imageUrl || pkg.image || "/images/default-omra.jpg",
        departures: getDepartureBadges(pkg),
      };
    });
});

const ramadanPackages = computed(() => {
  const packages = packagesData.value?.packages || [];
  return packages
    .filter((pkg) => pkg.isRamadan || pkg.category === "omra-ramadhan")
    .slice(0, 4) // Limit to 4 packages
    .sort((a, b) => getPackageMinPrice(a) - getPackageMinPrice(b))
    .map((pkg) => ({
      id: pkg.id,
      fullTitle: (pkg.title || pkg.name || "Omra Ramadhan").toUpperCase(),
      href: `/omra-ramadhan/${pkg.slug}`,
      image: pkg.imageUrl || pkg.image || "/images/default-omra.jpg",
      periods: getPeriodFromPackage(pkg),
    }));
});

// Returns the minimum price (in cents for Ramadhan, euros for regular) for sorting purposes
function getPackageMinPrice(pkg: Package): number {
  if (
    (pkg.isRamadan || pkg.category === "omra-ramadhan") &&
    pkg.hotelCombinations?.length
  ) {
    const departureDates = getRamadanDepartureDates(pkg);
    const prices = (pkg.hotelCombinations as any[])
      .filter((c: any) => c.isActive)
      .map((c: any) => getMinCombinationPrice(c, departureDates))
      .filter((p: any): p is number => p !== null && p > 0);
    if (prices.length > 0) return Math.min(...prices);
  }
  if (pkg.pricing?.minPrice) return pkg.pricing.minPrice * 100;
  if (pkg.pricing?.byRoom) {
    const prices = Object.values(pkg.pricing.byRoom)
      .map((r: any) => r.basePrice)
      .filter((p): p is number => typeof p === "number" && p > 0);
    if (prices.length > 0) return Math.min(...prices) * 100;
  }
  return pkg.basePrice ? pkg.basePrice * 100 : Infinity;
}

// Helper to extract departure badges for regular Omra packages
function getDepartureBadges(pkg: Package): string[] {
  const badges: string[] = [];

  if (pkg.inventoryRules && pkg.inventoryRules.length > 0) {
    // Use departureType field to distinguish Mensuel vs Hebdomadaire
    const hasMonthly = pkg.inventoryRules.some(
      (rule) => rule.departureType === "MONTHLY_GROUP",
    );
    const hasWeekly = pkg.inventoryRules.some(
      (rule) =>
        rule.departureType === "WEEKLY_INDIVIDUAL" ||
        (rule.departureType == null && rule.type === "recurring-weekly"),
    );
    const hasSpecific = pkg.inventoryRules.some(
      (rule) =>
        rule.type === "specific-dates" && rule.dates && rule.dates.length > 0,
    );

    if (hasMonthly) badges.push("Mensuel");
    if (hasWeekly) badges.push("Hebdomadaire");
    if (hasSpecific && badges.length === 0) {
      const specificDates = pkg.inventoryRules
        .filter(
          (rule) =>
            rule.type === "specific-dates" &&
            rule.dates &&
            rule.dates.length > 0,
        )
        .flatMap((rule) => rule.dates || [])
        .filter(Boolean)
        .sort();
      if (specificDates.length > 0) {
        const first = new Date(specificDates[0]!);
        const label = first.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        badges.push(
          specificDates.length > 1
            ? `${label} (+${specificDates.length - 1})`
            : label,
        );
      }
    }
  }

  return badges.length > 0
    ? badges
    : [pkg.departureSchedule || pkg.frequency || "Départs Hebdomadaires"];
}

// Helper to extract departure label for regular Omra packages
function getDepartureLabel(pkg: Package): string {
  return getDepartureBadges(pkg)[0] || "Départs Hebdomadaires";
}

// Helper to extract period info from package
function getPeriodFromPackage(pkg: Package): string[] {
  // Check ramadanDepartures first (multiple departure support)
  const deps = pkg.ramadanDepartures as
    | Array<{ date: string }>
    | null
    | undefined;
  if (Array.isArray(deps) && deps.length > 0) {
    return [...deps]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => {
        const date = new Date(d.date);
        return date.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      });
  }

  // Try fixedDepartureDate next (for Ramadhan packages)
  if (pkg.fixedDepartureDate) {
    const departureDate = new Date(pkg.fixedDepartureDate);
    const dateStr = departureDate.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return [dateStr];
  }

  // Fallback to availability dates
  if (pkg.availability?.from && pkg.availability?.to) {
    const from = new Date(pkg.availability.from);
    const to = new Date(pkg.availability.to);
    const fromStr = from.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
    const toStr = to.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
    return [`${fromStr} - ${toStr}`];
  }

  return ["Ramadhan 2026"];
}

// Computed Ramadhan dates from packages
const ramadhanInfo = computed(() => {
  const packages = packagesData.value?.packages || [];
  const ramadhanPkg = packages.find(
    (pkg) => pkg.isRamadan || pkg.category === "omra-ramadhan",
  );

  if (ramadhanPkg?.fixedDepartureDate && ramadhanPkg?.durationDays) {
    const startDate = new Date(ramadhanPkg.fixedDepartureDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + ramadhanPkg.durationDays);

    const year = startDate.getFullYear();
    const dateRange = `${startDate.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
    })} - ${endDate.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
    })}`;

    return {
      year,
      dateRange,
      title: `Ramadhan ${year}`,
    };
  }

  // Fallback to default values
  return {
    year: 2025,
    dateRange: "28 Février - 29 Mars",
    title: "Ramadhan 2025",
  };
});

const auth = useAuthStore();
const isAuthenticated = computed(() => !!auth.user);
const isAdmin = computed(() => auth.user?.role === "ADMIN");

// Role-aware paths for mobile menu
const mobileProfilePath = computed(() => {
  const role = ((auth.user?.role || "USER") as string)
    .toString()
    .trim()
    .toUpperCase();

  if (role === "ADMIN") {
    return "/admin/profile";
  } else if (role === "BOOKING_MANAGER") {
    return "/booking-manager/profile";
  }
  return "/dashboard/profile";
});

// Initialize notifications for authenticated users
const notifications = useNotifications();
onMounted(async () => {
  // Check Kratos session on mount
  // This will set auth.user if verified, or null if unverified/not logged in
  // It won't redirect - only middleware does that for protected routes
  try {
    await auth.checkKratosSession(); // Will return null for unverified, no redirect
  } catch (error: any) {
    // Silently fail - errors are already handled
  }

  if (isAuthenticated.value) {
    await notifications.initialize();
  }
});

// Re-initialize when user logs in
watch(isAuthenticated, async (newValue) => {
  if (newValue) {
    await notifications.initialize();
  }
});

// New unified auth modal state
const openAuth = ref(false);
const authTab = ref<"signin" | "signup">("signin");
const openAuthWith = (tab: "signin" | "signup") => {
  authTab.value = tab;
  openAuth.value = true;
};

const mobileMenuOpen = ref(false);
const openAccordion = ref<string | null>(null);

// Mega Menu State
const activeMegaMenu = ref<string | null>(null);
let megaMenuTimeout: ReturnType<typeof setTimeout> | null = null;

const openMegaMenu = (menu: string) => {
  if (megaMenuTimeout) {
    clearTimeout(megaMenuTimeout);
    megaMenuTimeout = null;
  }
  activeMegaMenu.value = menu;
};

const closeMegaMenu = () => {
  megaMenuTimeout = setTimeout(() => {
    activeMegaMenu.value = null;
  }, 150);
};

const keepMegaMenuOpen = () => {
  if (megaMenuTimeout) {
    clearTimeout(megaMenuTimeout);
    megaMenuTimeout = null;
  }
};

const closeMegaMenuImmediate = () => {
  if (megaMenuTimeout) {
    clearTimeout(megaMenuTimeout);
    megaMenuTimeout = null;
  }
  activeMegaMenu.value = null;
};

const toggleAccordion = (id: string) => {
  openAccordion.value = openAccordion.value === id ? null : id;
};

// Brochure modal state
const showBrochureModal = ref(false);
const brochureType = ref<"omra" | "ramadhan">("omra");

const openBrochure = (type: "omra" | "ramadhan") => {
  brochureType.value = type;
  showBrochureModal.value = true;
  closeMegaMenuImmediate();
};

const closeBrochureModal = () => {
  showBrochureModal.value = false;
};

const openBrochureMobile = (type: "omra" | "ramadhan") => {
  closeMobileMenu();
  brochureType.value = type;
  showBrochureModal.value = true;
};

const navigationItems: NavigationItem[] = [
  {
    label: "Omra",
    type: "dropdown",
    children: [
      { label: "Toutes les formules Omra", href: "/omra" },
      { label: "Omra Économique", href: "/omra/economique" },
      { label: "Omra Confort", href: "/omra/confort" },
      { label: "Omra VIP", href: "/omra/vip" },
    ],
  },
  {
    label: "Omra Ramadhan",
    type: "dropdown",
    children: [
      { label: "Toutes les formules Ramadhan", href: "/omra-ramadhan" },
      { label: "Première quinzaine", href: "/omra-ramadhan/premiere" },
      { label: "Seconde quinzaine", href: "/omra-ramadhan/seconde" },
      { label: "Intégral Ramadhan", href: "/omra-ramadhan/integral" },
    ],
  },
  {
    label: "Épargne",
    type: "dropdown",
    children: [
      { label: "Tous les plans d'épargne", href: "/epargne" },
      { label: "Épargne Omra", href: "/epargne/epargne-omra" },
      { label: "Épargne Hajj", href: "/epargne/epargne-hajj" },
    ],
  },
];

function goAccount() {
  const role = ((auth.user?.role || "USER") as string)
    .toString()
    .trim()
    .toUpperCase();

  if (role === "ADMIN") {
    navigateTo("/admin");
  } else if (role === "BOOKING_MANAGER") {
    navigateTo("/booking-manager");
  } else {
    navigateTo("/dashboard");
  }
}

async function onLogout() {
  // Clear auth state first
  auth.user = null;
  auth.sessionRestored = false;
  auth.verificationRequired = false;

  // Navigate to Kratos logout URL (this will clear the cookie and redirect back)
  await auth.logout();
  // Note: The above will navigate away, so code after this won't execute
}

// role-aware menu items (simple & efficient)
const userMenuItems = computed(() => {
  const role = ((auth.user?.role || "USER") as string)
    .toString()
    .trim()
    .toUpperCase();

  if (role === "ADMIN") {
    return [
      {
        label: "Dashboard",
        icon: ChartBarIcon,
        action: () => navigateTo("/admin"),
      },
      {
        label: "Mon compte",
        icon: UserIcon,
        action: () => navigateTo("/admin/profile"),
      },
      {
        label: "Se déconnecter",
        icon: ArrowRightOnRectangleIcon,
        danger: true,
        action: onLogout,
      },
    ];
  }

  if (role === "BOOKING_MANAGER") {
    return [
      {
        label: "Dashboard",
        icon: ChartBarIcon,
        action: () => navigateTo("/booking-manager"),
      },
      {
        label: "Mon compte",
        icon: UserIcon,
        action: () => navigateTo("/booking-manager/profile"),
      },
      {
        label: "Se déconnecter",
        icon: ArrowRightOnRectangleIcon,
        danger: true,
        action: onLogout,
      },
    ];
  }

  // default user
  return [
    {
      label: "Tableau de bord",
      icon: ChartBarIcon,
      action: () => navigateTo("/dashboard"),
    },
    {
      label: "Mon compte",
      icon: UserIcon,
      action: () => navigateTo("/dashboard/profile"),
    },
    {
      label: "Se déconnecter",
      icon: ArrowRightOnRectangleIcon,
      danger: true,
      action: onLogout,
    },
  ];
});

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  // Lock body scroll when menu is open
  if (mobileMenuOpen.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
    openAccordion.value = null;
  }
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
  openAccordion.value = null;
  document.body.style.overflow = "";
};

// Mobile-specific actions that close menu first
const goAccountMobile = () => {
  closeMobileMenu();
  goAccount();
};

const onLogoutMobile = async () => {
  closeMobileMenu();
  await onLogout();
};

const openAuthMobile = (tab: "signin" | "signup") => {
  closeMobileMenu();
  openAuthWith(tab);
};

// Close mobile menu when route changes
watch(
  () => useRoute().path,
  () => {
    closeMobileMenu();
    closeMegaMenuImmediate();
  },
);

// Cleanup on unmount
onUnmounted(() => {
  document.body.style.overflow = "";
  if (megaMenuTimeout) {
    clearTimeout(megaMenuTimeout);
  }
});
</script>
